import { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Search, X, Mail, User, Loader2, Users, UserPlus } from "lucide-react";
import { cn } from "@/lib/utils";

interface SearchResult {
  id: string;
  name: string;
  email: string | null;
  skillLevel: number | null;
  isFriend?: boolean;
}

interface SelectedPlayer {
  id?: string;
  name: string;
  email?: string;
  isInvite?: boolean;
  isFriend?: boolean;
}

interface PlayerSearchInputProps {
  value: SelectedPlayer | null;
  onChange: (player: SelectedPlayer | null) => void;
  placeholder?: string;
  excludeIds?: string[];
  label?: string;
}

function getInitials(name: string): string {
  return name
    .split(" ")
    .map(n => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) || "?";
}

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export function PlayerSearchInput({ 
  value, 
  onChange, 
  placeholder = "Buscar jugador...",
  excludeIds = [],
  label
}: PlayerSearchInputProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [showInviteInput, setShowInviteInput] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const { data: searchResults = [], isFetching } = useQuery<SearchResult[]>({
    queryKey: ['/api/players/search', searchQuery],
    queryFn: async () => {
      if (searchQuery.length < 2) return [];
      const res = await fetch(`/api/players/search?q=${encodeURIComponent(searchQuery)}`, {
        credentials: 'include'
      });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: searchQuery.length >= 2,
    staleTime: 30000,
  });

  const filteredResults = searchResults.filter(r => !excludeIds.includes(r.id));

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setShowInviteInput(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (player: SearchResult) => {
    onChange({
      id: player.id,
      name: player.name,
      email: player.email || undefined,
      isFriend: player.isFriend,
    });
    setSearchQuery("");
    setIsOpen(false);
    setShowInviteInput(false);
  };

  const handleInvite = () => {
    if (isValidEmail(inviteEmail)) {
      onChange({
        name: inviteEmail.split("@")[0],
        email: inviteEmail,
        isInvite: true,
      });
      setInviteEmail("");
      setSearchQuery("");
      setIsOpen(false);
      setShowInviteInput(false);
    }
  };

  const handleClear = () => {
    onChange(null);
    setSearchQuery("");
    setShowInviteInput(false);
  };

  if (value) {
    return (
      <div className="relative">
        {label && <span className="text-xs text-muted-foreground mb-1 block">{label}</span>}
        <div className={cn(
          "flex items-center gap-3 p-3 rounded-xl border-2 transition-all",
          value.isInvite 
            ? "border-amber-500/50 bg-amber-500/5" 
            : value.isFriend 
              ? "border-green-500/50 bg-green-500/5"
              : "border-primary/50 bg-background"
        )}>
          <Avatar className={cn(
            "h-10 w-10 border-2",
            value.isInvite 
              ? "border-amber-500/30"
              : value.isFriend 
                ? "border-green-500/30"
                : "border-primary/30"
          )}>
            <AvatarFallback className={cn(
              "text-sm font-bold",
              value.isInvite 
                ? "bg-amber-500/20 text-amber-600" 
                : value.isFriend
                  ? "bg-green-500/20 text-green-700 dark:text-green-400"
                  : "bg-primary/20 text-primary"
            )}>
              {value.isInvite ? <Mail className="h-4 w-4" /> : getInitials(value.name)}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium text-sm truncate">{value.name}</span>
              {value.isFriend && (
                <Badge variant="secondary" className="shrink-0 bg-green-500/20 text-green-700 dark:text-green-400 border-0 text-[10px] px-1.5 py-0">
                  <Users className="h-3 w-3 mr-0.5" />
                  Amigo
                </Badge>
              )}
            </div>
            {value.isInvite && (
              <div className="text-xs text-amber-600">Invitación pendiente</div>
            )}
            {value.email && !value.isInvite && (
              <div className="text-xs text-muted-foreground truncate">{value.email}</div>
            )}
          </div>
          <Button 
            type="button"
            variant="ghost" 
            size="icon" 
            className="h-8 w-8 shrink-0"
            onClick={handleClear}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      {label && <span className="text-xs text-muted-foreground mb-1 block">{label}</span>}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          ref={inputRef}
          type="text"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setIsOpen(true);
            setShowInviteInput(false);
          }}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder}
          className="pl-10 pr-10 h-12 text-base rounded-xl"
        />
        {isFetching && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground animate-spin" />
        )}
      </div>

      {isOpen && searchQuery.length >= 2 && (
        <div className="absolute z-50 w-full mt-2 bg-popover border rounded-xl shadow-lg overflow-hidden">
          {filteredResults.length > 0 ? (
            <div className="max-h-[280px] overflow-y-auto">
              {filteredResults.map((player) => (
                <button
                  key={player.id}
                  type="button"
                  onClick={() => handleSelect(player)}
                  className={cn(
                    "w-full flex items-center gap-3 p-3 hover:bg-accent transition-colors text-left",
                    player.isFriend && "bg-green-500/5"
                  )}
                >
                  <Avatar className={cn(
                    "h-10 w-10 border",
                    player.isFriend ? "border-green-500/50" : "border-primary/20"
                  )}>
                    <AvatarFallback className={cn(
                      "text-sm",
                      player.isFriend ? "bg-green-500/20 text-green-700 dark:text-green-400" : "bg-primary/10 text-primary"
                    )}>
                      {getInitials(player.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{player.name}</span>
                      {player.isFriend && (
                        <Badge variant="secondary" className="shrink-0 bg-green-500/20 text-green-700 dark:text-green-400 border-0 text-[10px] px-1.5 py-0">
                          <Users className="h-3 w-3 mr-0.5" />
                          Amigo
                        </Badge>
                      )}
                    </div>
                    {player.email && (
                      <div className="text-xs text-muted-foreground truncate">{player.email}</div>
                    )}
                  </div>
                  {player.skillLevel !== null && (
                    <Badge variant="secondary" className="shrink-0">
                      Nivel {player.skillLevel.toFixed(1)}
                    </Badge>
                  )}
                </button>
              ))}
            </div>
          ) : !isFetching ? (
            <div className="p-4">
              {!showInviteInput ? (
                <div className="text-center space-y-3">
                  <div className="flex justify-center">
                    <User className="h-10 w-10 text-muted-foreground/50" />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    No encontramos a "{searchQuery}"
                  </p>
                  <Button 
                    type="button"
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowInviteInput(true)}
                    className="w-full"
                  >
                    <Mail className="h-4 w-4 mr-2" />
                    Invitar por correo
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm font-medium">Invitar jugador</p>
                  <p className="text-xs text-muted-foreground">
                    Ingresa el correo electrónico para enviar una invitación
                  </p>
                  <Input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="correo@ejemplo.com"
                    className="h-10"
                    autoFocus
                  />
                  <div className="flex gap-2">
                    <Button 
                      type="button"
                      variant="ghost" 
                      size="sm"
                      onClick={() => setShowInviteInput(false)}
                      className="flex-1"
                    >
                      Cancelar
                    </Button>
                    <Button 
                      type="button"
                      size="sm"
                      onClick={handleInvite}
                      disabled={!isValidEmail(inviteEmail)}
                      className="flex-1 bg-amber-500 hover:bg-amber-600 text-white"
                    >
                      <Mail className="h-4 w-4 mr-1" />
                      Invitar
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
